import { Fragment } from "react";
import { motion } from "framer-motion";
import { ISupportMessage } from "../models/supportChatModels";
import styles from "./SupportChat.module.scss";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { Icon, IconType } from "../../../common/icons/Icon";
import { useSupportChatMessage } from "./hooks/useSupportChatMessage";

interface ISupportChatMessageProps {
  message: ISupportMessage;
  isCurrentUser: boolean;
  isGrouped?: boolean;
  hideAvatar?: boolean;
  showTimestamp?: boolean;
  showTail?: boolean;
}

export const SupportChatMessage = ({
  message,
  isCurrentUser,
  isGrouped = false,
  hideAvatar = false,
  showTimestamp = true,
  showTail = true,
}: ISupportChatMessageProps) => {
  const { textParts, initials, gradient } = useSupportChatMessage(message);

  return (
    <motion.div
      className={`${styles.message} ${isCurrentUser ? styles.message_own : styles.message_other} ${isGrouped ? styles.message_grouped : ""}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
    >
      {!isCurrentUser && (
        <div
          className={`${styles.message_avatar} ${hideAvatar ? styles.message_avatar_hidden : ""}`}
          style={!message.senderProfilePhotoUrl ? { background: gradient } : undefined}
        >
          {message.senderProfilePhotoUrl ? (
            <img src={message.senderProfilePhotoUrl} alt={message.senderName} />
          ) : (
            initials
          )}
        </div>
      )}

      <div className={`${styles.message_content_wrapper} ${isCurrentUser ? styles.message_content_wrapper_own : styles.message_content_wrapper_other}`}>
        <div className={`${styles.message_bubble} ${isCurrentUser ? styles.message_bubble_own : styles.message_bubble_other} ${!showTail ? (isCurrentUser ? styles.message_bubble_own_round : styles.message_bubble_other_round) : ""}`}>
          {textParts.length > 0 && (
            <div className={styles.message_bubble_text}>
              {textParts.map((part, index) =>
                part.type === "link" ? (
                  <a
                    key={index}
                    href={part.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.message_link}
                  >
                    {part.value}
                  </a>
                ) : (
                  <Fragment key={index}>{part.value}</Fragment>
                ),
              )}
            </div>
          )}

          {message.attachments.length > 0 && (
            <div className={styles.message_attachments}>
              {message.attachments.map((att) => {
                const isImage = att.contentType?.startsWith("image/");

                if (isImage) {
                  return (
                    <div key={att.id} className={styles.message_attachment_image_wrapper}>
                      <img
                        src={att.url}
                        alt={att.fileName}
                        className={styles.message_attachment_img}
                        onClick={() => window.open(att.url, "_blank")}
                      />
                      <div className={styles.message_attachment_image_name}>{att.fileName}</div>
                    </div>
                  );
                }

                return (
                  <a
                    key={att.id}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.message_attachment}
                  >
                    <Icon type={IconType.Paperclip} width="13px" height="13px" />
                    <span className={styles.message_attachment_name}>{att.fileName}</span>
                    {att.fileSize > 0 && (
                      <span className={styles.message_attachment_size}>
                        {formatFileSize(att.fileSize)}
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {showTimestamp && (
          <div className={`${styles.message_time} ${isCurrentUser ? styles.message_time_own : styles.message_time_other}`}>
            {!isCurrentUser && (
              <><span>{message.senderName}</span><span className={styles.message_time_dot}>&middot;</span></>
            )}
            {dateUtility.formatRelativeDateTime(message.sentAt)}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
